import { useEffect } from "react";

export const useRefreshProtection = () => {
  useEffect(() => {
    if ((window as any).refreshProtectionInitialized) return;
    (window as any).refreshProtectionInitialized = true;
    const getActiveVideos = () => JSON.parse(localStorage.getItem("active_videos") || "{}");

    const hasActiveVideos = () => !!Object.keys(getActiveVideos()).length;

    const getCancelMessage = () => {
      const activeVideos = getActiveVideos();
      const uploadingEntries = Object.entries(activeVideos)
        .map(([_, video]: [string, any]) => `Entry #${video.entry_number}`)
        .join("\n");
      
      if (uploadingEntries) {
        return `Talktape upload is in progress for:\n${uploadingEntries}\nRefreshing will CANCEL the uploads.\nDo you want to cancel the uploads and refresh?`;
      }
    };

    const cancelAllActiveUploads = async () => {
      const activeVideos = getActiveVideos();
      const performanceIds = Object.keys(activeVideos);
      if (performanceIds.length === 0) return;
      try {
        fetch(
          `${import.meta.env.VITE_API_URL}/performances/mux/delete?performance_id=${performanceIds.join(',')}`,
          {
            method: "PUT",
            keepalive: true,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            }
          }
        );
        localStorage.removeItem("active_videos");
      } catch (error) {
        console.error(`Failed to cancel batch uploads:`, error);
        localStorage.removeItem("active_videos");
      }
    };

    const handleUnload = () => {
      if (hasActiveVideos()) {
        cancelAllActiveUploads();
      }
    };


    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasActiveVideos()) return;
      e.preventDefault();
      const message = getCancelMessage();
      return message;
    };


    const confirmAndReload = () => {
      const message = getCancelMessage();
      if (message && window.confirm(message)) {
        cancelAllActiveUploads().finally(() => {
          window.location.reload();
        });
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!hasActiveVideos()) return;

      const isRefresh =
        e.key === "F5" ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r");

      if (!isRefresh) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      confirmAndReload();
    };

    const handleClick = (e: MouseEvent) => {
      if (!hasActiveVideos()) return;

      const el = (e.target as HTMLElement).closest(
        'a[href], button, [role="button"]'
      ) as HTMLElement | null;

      if (!el) return;

      const text = (
        (el.textContent || '') +
        (el.title || '') +
        (el.getAttribute("aria-label") || '')
      ).toLowerCase();

      if (!text.includes("refresh") && !text.includes("reload")) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      confirmAndReload();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    document.addEventListener('click', handleClick, { capture: true, passive: false });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('click', handleClick, true);
      (window as any).refreshProtectionInitialized = false;
    };
  }, []);
};